import { WorkspaceDataplane } from "src/clients/flow-api";
import {
  AssumableRole,
  AssumableRoleScope,
  OrganizationInDB,
} from "src/clients/taktile-api";
import { pluralize } from "src/utils/stringUtils";

const EVERY = "*";

export type ScopedWorkspace = {
  id: string;
  name: string;
  organizationName: string;
  /** The role the caller actually holds that reaches this workspace. */
  via: string;
};

export type RoleReach = {
  /** The role has no organization or workspace dimension at all. */
  platformWide: boolean;
  /** Covers every workspace in every organization; nothing is enumerated. */
  allWorkspaces: boolean;
  /** Covers every organization at the organization level, without workspaces. */
  allOrganizations: boolean;
  /** Organizations the role covers as a whole, having no workspace argument. */
  organizationNames: string[];
  /**
   * Organizations where the role covers every workspace but none are known to
   * the caller, so there is nothing to enumerate.
   */
  everyWorkspaceIn: string[];
  workspaces: ScopedWorkspace[];
};

const scopeWorkspaceId = (scope: AssumableRoleScope): string | undefined =>
  scope.workspace_id ?? undefined;

const scopeOrganizationId = (scope: AssumableRoleScope): string | undefined =>
  scope.organization_id ?? undefined;

/**
 * Turn the (organization, workspace) scopes the backend reports for a role
 * into the concrete workspaces the caller would get by assuming it.
 */
export const resolveRoleReach = (
  role: AssumableRole,
  workspaces: WorkspaceDataplane[],
  organizations: OrganizationInDB[],
): RoleReach => {
  if (role.arg_names.length === 0) {
    return {
      platformWide: true,
      allWorkspaces: false,
      allOrganizations: false,
      organizationNames: [],
      everyWorkspaceIn: [],
      workspaces: [],
    };
  }

  const organizationName = (id: string | undefined): string =>
    organizations.find((org) => org.id === id)?.name ?? "Unknown organization";

  const scopeWorkspaces = (scope: AssumableRoleScope): ScopedWorkspace[] => {
    const base = {
      organizationName: organizationName(scopeOrganizationId(scope)),
      via: scope.via,
    };
    if (scope.workspace_id === EVERY) {
      return workspaces
        .filter(
          (workspace) =>
            workspace.organization_id === scopeOrganizationId(scope),
        )
        .map((workspace) => ({
          ...base,
          id: workspace.id,
          name: workspace.name,
        }));
    }
    const match = workspaces.find(
      (workspace) => workspace.id === scope.workspace_id,
    );
    return [
      {
        ...base,
        id: scope.workspace_id ?? "",
        name: match?.name ?? "N/A (probably deleted)",
      },
    ];
  };

  const wildcardScopes = role.scopes.filter(
    (scope) => scopeOrganizationId(scope) === EVERY,
  );
  const allWorkspaces = wildcardScopes.some(
    (scope) => scopeWorkspaceId(scope) !== undefined,
  );
  const allOrganizations = wildcardScopes.some(
    (scope) => scopeWorkspaceId(scope) === undefined,
  );

  const workspaceScopes = allWorkspaces
    ? []
    : role.scopes.filter(
        (scope) =>
          scopeOrganizationId(scope) !== EVERY &&
          scopeWorkspaceId(scope) !== undefined,
      );

  const expandedScopes = workspaceScopes.map((scope) => ({
    scope,
    rows: scopeWorkspaces(scope),
  }));

  const everyWorkspaceIn = Array.from(
    new Set(
      expandedScopes
        .filter(
          ({ scope, rows }) =>
            scope.workspace_id === EVERY && rows.length === 0,
        )
        .map(({ scope }) => organizationName(scopeOrganizationId(scope))),
    ),
  ).sort();

  const deduplicated = new Map(
    expandedScopes
      .flatMap(({ rows }) => rows)
      .map((workspace) => [workspace.id, workspace] as const),
  );

  return {
    platformWide: false,
    allWorkspaces,
    allOrganizations,
    organizationNames: allOrganizations
      ? []
      : Array.from(
          new Set(
            role.scopes
              .filter(
                (scope) =>
                  scopeWorkspaceId(scope) === undefined &&
                  scopeOrganizationId(scope) !== undefined &&
                  scopeOrganizationId(scope) !== EVERY,
              )
              .map((scope) => organizationName(scopeOrganizationId(scope))),
          ),
        ).sort(),
    everyWorkspaceIn,
    workspaces: Array.from(deduplicated.values()).sort(
      (a, b) =>
        a.organizationName.localeCompare(b.organizationName) ||
        a.name.localeCompare(b.name),
    ),
  };
};

const describeOrganizationReach = (
  allOrganizations: boolean,
  organizationNames: string[],
): string | null => {
  if (allOrganizations) {
    return "Every organization";
  }
  if (organizationNames.length > 0) {
    return pluralize(organizationNames.length, "organization");
  }
  return null;
};

/** One-line summary of where a role would apply, for the collapsed row. */
export const describeRoleReach = (reach: RoleReach): string => {
  const {
    platformWide,
    allWorkspaces,
    allOrganizations,
    organizationNames,
    everyWorkspaceIn,
    workspaces,
  } = reach;

  if (platformWide) {
    return "Platform-wide";
  }
  if (allWorkspaces) {
    return "Every workspace in every organization";
  }

  const parts = [
    describeOrganizationReach(allOrganizations, organizationNames),
    everyWorkspaceIn.length > 0
      ? `every workspace in ${everyWorkspaceIn.join(", ")}`
      : null,
    workspaces.length > 0 ? pluralize(workspaces.length, "workspace") : null,
  ].filter((part) => part !== null);

  return parts.length > 0 ? parts.join(" · ") : "No direct access";
};
